import { BbFahAssistant, BbFahToggleButton } from "@/components/admin/bb-fah-assistant";
import Image from "@/compat/image";
import Link from "@/compat/link";
import { usePathname, useRouter } from "@/compat/navigation";
import { useEffect, useRef, useState } from "react";
import { AdminRegionSelect } from "@/components/admin/admin-region-select";
import { AdminSidebar } from "@/components/admin/admin-sidebar";
import { ThemeToggle } from "@/components/theme-toggle";
import { applySessionRegion } from "@/lib/admin-region";
import { tokenHasAnyRole } from "@/lib/jwt-roles";
import { ChevronDown, Ellipsis, LogOut, Menu, Pin, PinOff, X } from "lucide-react";
import { apiFetch } from "@/lib/api/client";
import {
  ADMIN_SET_TAB_TITLE_EVENT,
  getAdminTabLabel,
  isAdminWorkspacePath,
  normalizeAdminPath,
} from "@/lib/admin-workspace-tabs";
import { refreshAccessToken } from "@/lib/auth-session";
import { getDisplayNameFromToken, getJwtExpiresAtMs, getRegionFromToken } from "@/lib/jwt-claims";
import { alertSuccessModal, confirmAction, notify } from "@/lib/notify";
import { useAuthStore } from "@/stores/auth-store";
import { useAdminAccessStore } from "@/stores/admin-access-store";

type WorkspaceTab = { path: string; label: string; pinned: boolean };
const ADMIN_WORKSPACE_TABS_KEY = "admin.workspace.tabs.v1";
const ADMIN_WORKSPACE_HINT_SEEN_KEY = "admin.workspace.tabHintSeen.v1";
const ADMIN_WORKSPACE_LAST_ACTIVE_KEY = "admin.workspace.lastActive.v1";
const ADMIN_WORKSPACE_MAX_TABS = 15;

function pinnedFirst(tabs: WorkspaceTab[]): WorkspaceTab[] {
  const pinned = tabs.filter((t) => t.pinned);
  const unpinned = tabs.filter((t) => !t.pinned);
  return [...pinned, ...unpinned];
}

function enforceWorkspaceTabLimit(tabs: WorkspaceTab[]): {
  tabs: WorkspaceTab[];
  evicted: number;
} {
  if (tabs.length <= ADMIN_WORKSPACE_MAX_TABS) return { tabs, evicted: 0 };
  const pinned = tabs.filter((t) => t.pinned);
  const unpinned = tabs.filter((t) => !t.pinned);
  const allowedUnpinned = Math.max(0, ADMIN_WORKSPACE_MAX_TABS - pinned.length);
  if (allowedUnpinned >= unpinned.length) return { tabs, evicted: 0 };
  const keepUnpinned = unpinned.slice(unpinned.length - allowedUnpinned);
  return {
    tabs: [...pinned, ...keepUnpinned],
    evicted: unpinned.length - keepUnpinned.length,
  };
}

export function AdminShell({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const logout = useAuthStore((s) => s.logout);
  const router = useRouter();
  const pathname = usePathname();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [bbFahOpen, setBbFahOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [tabsHydrated, setTabsHydrated] = useState(false);
  const [workspaceTabs, setWorkspaceTabs] = useState<WorkspaceTab[]>([]);
  const [draggingPath, setDraggingPath] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [tabMenu, setTabMenu] = useState<{ path: string; x: number; y: number } | null>(null);
  const [showTabHint, setShowTabHint] = useState(false);
  const [workspaceEvictedNotice, setWorkspaceEvictedNotice] = useState(0);
  const [restoredLastTab, setRestoredLastTab] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const tabMenuRef = useRef<HTMLDivElement | null>(null);
  const mainRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const unsub = useAuthStore.persist.onFinishHydration(() => {
      setHydrated(true);
    });
    if (useAuthStore.persist.hasHydrated()) {
      setHydrated(true);
    }
    return unsub;
  }, []);

  useEffect(() => {
    if (!token) return;
    applySessionRegion({
      region: getRegionFromToken(token),
      lockRegion: tokenHasAnyRole(token, ["Logistic"]),
    });
  }, [token]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(ADMIN_WORKSPACE_TABS_KEY);
      if (!raw) {
        setTabsHydrated(true);
        return;
      }
      const parsed = JSON.parse(raw) as Array<Partial<WorkspaceTab>>;
      const restored = parsed.flatMap((tab) => {
        const path = normalizeAdminPath(String(tab.path ?? ""));
        if (!path || !isAdminWorkspacePath(path)) return [];
        const row: WorkspaceTab = {
          path,
          label: String(tab.label ?? getAdminTabLabel(path)),
          pinned: Boolean(tab.pinned),
        };
        return [row];
      });
      const limited = enforceWorkspaceTabLimit(pinnedFirst(restored));
      setWorkspaceTabs(limited.tabs);
      if (limited.evicted > 0) setWorkspaceEvictedNotice(limited.evicted);
    } catch {
      setWorkspaceTabs([]);
    } finally {
      setTabsHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (!tabsHydrated || typeof window === "undefined") return;
    window.localStorage.setItem(ADMIN_WORKSPACE_TABS_KEY, JSON.stringify(workspaceTabs));
  }, [workspaceTabs, tabsHydrated]);

  useEffect(() => {
    if (workspaceEvictedNotice <= 0) return;
    notify.info(
      `Workspace limited to ${ADMIN_WORKSPACE_MAX_TABS} tabs. Closed ${workspaceEvictedNotice} oldest unpinned tab(s).`
    );
    setWorkspaceEvictedNotice(0);
  }, [workspaceEvictedNotice]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const seen = window.localStorage.getItem(ADMIN_WORKSPACE_HINT_SEEN_KEY) === "1";
    setShowTabHint(!seen);
  }, []);

  const currentPath = normalizeAdminPath(pathname ?? "/dashboard");

  function routeNeedsUnsavedGuard(path: string): boolean {
    return /\/(new|edit)(\/|$)/.test(path);
  }

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isAdminWorkspacePath(currentPath)) return;
    window.localStorage.setItem(ADMIN_WORKSPACE_LAST_ACTIVE_KEY, currentPath);
  }, [currentPath]);

  useEffect(() => {
    if (!routeNeedsUnsavedGuard(currentPath)) {
      setDirty(false);
    }
  }, [currentPath]);

  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    const onChange = (event: Event) => {
      if (!routeNeedsUnsavedGuard(currentPath)) return;
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement)) {
        return;
      }
      setDirty(true);
    };
    const onSubmit = () => {
      setDirty(false);
    };
    main.addEventListener("change", onChange, true);
    main.addEventListener("input", onChange, true);
    main.addEventListener("submit", onSubmit, true);
    return () => {
      main.removeEventListener("change", onChange, true);
      main.removeEventListener("input", onChange, true);
      main.removeEventListener("submit", onSubmit, true);
    };
  }, [currentPath]);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty || !routeNeedsUnsavedGuard(currentPath)) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty, currentPath]);

  useEffect(() => {
    if (!tabsHydrated || restoredLastTab || typeof window === "undefined") return;
    setRestoredLastTab(true);
    if (currentPath !== "/dashboard") return;
    const last = normalizeAdminPath(
      window.localStorage.getItem(ADMIN_WORKSPACE_LAST_ACTIVE_KEY) ?? ""
    );
    if (!last || !isAdminWorkspacePath(last) || last === currentPath) return;
    if (!workspaceTabs.some((tab) => tab.path === last)) return;
    router.replace(last);
  }, [tabsHydrated, restoredLastTab, currentPath, workspaceTabs, router]);

  useEffect(() => {
    if (!tabsHydrated) return;
    const currentPath = normalizeAdminPath(pathname ?? "/dashboard");
    if (!isAdminWorkspacePath(currentPath)) return;
    setWorkspaceTabs((prev) => {
      const existingIdx = prev.findIndex((tab) => tab.path === currentPath);
      const nextLabel = getAdminTabLabel(currentPath);
      if (existingIdx >= 0) {
        if (prev[existingIdx]?.label === nextLabel) return prev;
        const next = [...prev];
        next[existingIdx] = { ...next[existingIdx], label: nextLabel };
        return next;
      }
      const limited = enforceWorkspaceTabLimit(
        pinnedFirst([...prev, { path: currentPath, label: nextLabel, pinned: false }])
      );
      if (limited.evicted > 0) setWorkspaceEvictedNotice((n) => n + limited.evicted);
      return limited.tabs;
    });
  }, [pathname, tabsHydrated]);

  useEffect(() => {
    const onSetTabTitle = (event: Event) => {
      const custom = event as CustomEvent<{ path?: string; label?: string }>;
      const path = normalizeAdminPath(String(custom.detail?.path ?? ""));
      const label = String(custom.detail?.label ?? "").trim();
      if (!path || !label) return;
      setWorkspaceTabs((prev) => {
        const idx = prev.findIndex((tab) => tab.path === path);
        if (idx < 0) return prev;
        if (prev[idx]?.label === label) return prev;
        const next = [...prev];
        next[idx] = { ...next[idx], label };
        return next;
      });
    };
    window.addEventListener(ADMIN_SET_TAB_TITLE_EVENT, onSetTabTitle as EventListener);
    return () => {
      window.removeEventListener(ADMIN_SET_TAB_TITLE_EVENT, onSetTabTitle as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    if (!token) {
      useAdminAccessStore.getState().clear();
      router.replace("/login");
      return;
    }
    void useAdminAccessStore.getState().load();
  }, [hydrated, token, router]);

  useEffect(() => {
    if (!hydrated || !token || !refreshToken) return;
    const expMs = getJwtExpiresAtMs(token);
    if (!expMs) return;
    if (Date.now() < expMs - 120_000) return;
    void refreshAccessToken().then((ok) => {
      if (!ok) {
        logout();
        router.replace("/login");
      }
    });
  }, [hydrated, token, refreshToken, logout, router]);

  useEffect(() => {
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    main.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileNavOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!tabMenu) return;
    const onDown = (e: MouseEvent) => {
      if (tabMenuRef.current && tabMenuRef.current.contains(e.target as Node)) return;
      setTabMenu(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setTabMenu(null);
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [tabMenu]);

  function dismissTabHint() {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(ADMIN_WORKSPACE_HINT_SEEN_KEY, "1");
    }
    setShowTabHint(false);
  }

  function openTabMenu(path: string, x: number, y: number) {
    dismissTabHint();
    setTabMenu({ path, x, y });
  }

  const displayName = getDisplayNameFromToken(token);

  async function confirmDiscardIfDirty(nextPath: string): Promise<boolean> {
    if (!dirty || !routeNeedsUnsavedGuard(currentPath) || nextPath === currentPath) return true;
    return confirmAction({
      title: "Discard unsaved changes?",
      text: "You have unsaved changes on this page. Switching tabs may lose your edits.",
      confirmButtonText: "Discard and continue",
      icon: "warning",
    });
  }

  function openWorkspaceRoute(path: string, label?: string) {
    const cleanPath = normalizeAdminPath(path);
    if (!isAdminWorkspacePath(cleanPath)) return;
    setWorkspaceTabs((prev) => {
      const idx = prev.findIndex((tab) => tab.path === cleanPath);
      const nextLabel = label ?? getAdminTabLabel(cleanPath);
      if (idx >= 0) {
        if (prev[idx]?.label === nextLabel) return prev;
        const next = [...prev];
        next[idx] = { ...next[idx], label: nextLabel };
        return next;
      }
      const limited = enforceWorkspaceTabLimit(
        pinnedFirst([...prev, { path: cleanPath, label: nextLabel, pinned: false }])
      );
      if (limited.evicted > 0) setWorkspaceEvictedNotice((n) => n + limited.evicted);
      return limited.tabs;
    });
  }

  async function openExistingTab(path: string) {
    setTabMenu(null);
    const ok = await confirmDiscardIfDirty(path);
    if (!ok) return;
    router.push(path, { scroll: false });
  }

  async function closeWorkspaceTab(path: string) {
    setTabMenu(null);
    if (path === currentPath) {
      const candidate = workspaceTabs.find((tab) => tab.path !== path);
      const ok = await confirmDiscardIfDirty(candidate?.path ?? "/dashboard");
      if (!ok) return;
    }
    setWorkspaceTabs((prev) => {
      const idx = prev.findIndex((tab) => tab.path === path);
      if (idx < 0) return prev;
      const next = prev.filter((tab) => tab.path !== path);
      if (next.length === 0) {
        router.push("/dashboard", { scroll: false });
        return [];
      }
      if (currentPath === path) {
        const fallback = next[Math.max(0, idx - 1)] ?? next[0];
        if (fallback) router.push(fallback.path, { scroll: false });
      }
      return next;
    });
  }

  function togglePin(path: string) {
    setTabMenu(null);
    setWorkspaceTabs((prev) =>
      pinnedFirst(prev.map((tab) => (tab.path === path ? { ...tab, pinned: !tab.pinned } : tab)))
    );
  }

  async function closeOtherTabs(keepPath: string = currentPath) {
    setTabMenu(null);
    const keep = workspaceTabs.filter((tab) => tab.path === keepPath || tab.pinned);
    setWorkspaceTabs(keep);
    if (currentPath !== keepPath) {
      const ok = await confirmDiscardIfDirty(keepPath);
      if (ok) router.push(keepPath, { scroll: false });
    }
  }

  async function closeAllTabs() {
    setTabMenu(null);
    const ok = await confirmDiscardIfDirty("/dashboard");
    if (!ok) return;
    const pinned = workspaceTabs.filter((tab) => tab.pinned);
    setWorkspaceTabs(pinned);
    if (pinned.length > 0) {
      router.push(pinned[0]!.path, { scroll: false });
      return;
    }
    router.push("/dashboard", { scroll: false });
  }

  function reorderTabs(fromPath: string, toPath: string) {
    if (fromPath === toPath) return;
    setWorkspaceTabs((prev) => {
      const fromIndex = prev.findIndex((tab) => tab.path === fromPath);
      const toIndex = prev.findIndex((tab) => tab.path === toPath);
      if (fromIndex < 0 || toIndex < 0) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      if (!moved) return prev;
      next.splice(toIndex, 0, moved);
      return next;
    });
  }

  if (!hydrated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface text-muted">
        Loading…
      </div>
    );
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface text-muted">
        Redirecting…
      </div>
    );
  }

  return (
    <div className="flex h-dvh min-h-0 min-w-0 max-w-full overflow-hidden bg-surface text-foreground">
      {mobileNavOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-overlay lg:hidden"
          aria-label="Close menu"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      {/* Left sidebar — legacy `side-menu`; navigation only */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex h-dvh min-h-0 w-72 min-w-0 shrink-0 flex-col border-r border-border bg-sidebar transition-transform duration-200 lg:static lg:translate-x-0 ${
          mobileNavOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <AdminSidebar
          onNavigate={() => setMobileNavOpen(false)}
          onOpenRoute={(path, label) => openWorkspaceRoute(path, label)}
        />
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden lg:ml-0">
        <div
          role="banner"
          className="admin-app-topbar relative z-50 min-h-14 shrink-0 gap-x-3 border-b border-border bg-topbar/95 px-3 backdrop-blur-sm sm:gap-x-4 sm:px-4"
        >
          <div className="flex min-w-0 flex-none flex-nowrap items-center gap-2 sm:gap-3">
            <button
              type="button"
              className="shrink-0 rounded-lg p-2 text-foreground-secondary hover:bg-hover-subtle lg:hidden"
              aria-label="Open menu"
              onClick={() => setMobileNavOpen(true)}
            >
              <Menu className="h-5 w-5" strokeWidth={1.75} aria-hidden />
            </button>
            <Link
              href="/dashboard"
              scroll={false}
              className="relative block h-9 w-[120px] shrink-0 sm:w-[132px] md:h-11 md:w-[220px]"
            >
              <Image
                src="/images/logo_pink_small.png"
                alt="Xmegami"
                fill
                className="object-contain object-left md:hidden"
                sizes="132px"
                priority
              />
              <Image
                src="/images/logo_pink.png"
                alt="Xmegami"
                fill
                className="hidden object-contain object-left md:block"
                sizes="220px"
                priority
              />
            </Link>
          </div>
          <div className="flex shrink-0 flex-nowrap items-center justify-end gap-2 text-sm sm:gap-3 md:gap-4">
            <AdminRegionSelect hidden={tokenHasAnyRole(token, ["Logistic"])} />
            <ThemeToggle />
            <BbFahToggleButton open={bbFahOpen} onToggle={() => setBbFahOpen((o) => !o)} />
            <div ref={menuRef} className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((o) => !o)}
                className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-1.5 text-muted transition-colors hover:bg-hover-subtle hover:text-foreground"
                aria-expanded={menuOpen}
                aria-haspopup="menu"
              >
                <span>
                  Welcome
                  {displayName ? (
                    <span className="text-foreground">, {displayName}</span>
                  ) : null}
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 opacity-70" strokeWidth={1.75} aria-hidden />
              </button>
              {menuOpen ? (
                <div
                  role="menu"
                  className="absolute right-0 z-50 mt-1 min-w-[12rem] rounded-lg border border-border bg-topbar py-1 shadow-lg"
                >
                  <Link
                    role="menuitem"
                    href="/change-password"
                    className="flex w-full px-3 py-2 text-sm text-foreground hover:bg-hover-subtle"
                    onClick={() => setMenuOpen(false)}
                  >
                    Change password
                  </Link>
                  <div className="my-1 border-t border-border" role="presentation" />
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-hover-subtle"
                    onClick={() => {
                      setMenuOpen(false);
                      logout();
                      router.replace("/login");
                    }}
                  >
                    <LogOut className="h-4 w-4 shrink-0 opacity-80" strokeWidth={1.75} aria-hidden />
                    Logout
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div className="border-b border-border bg-topbar/70 px-3 py-2 sm:px-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto">
            {workspaceTabs.length === 0 ? (
              <button
                type="button"
                onClick={() => router.push("/dashboard", { scroll: false })}
                className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs text-muted hover:bg-hover-subtle"
              >
                Open dashboard tab
              </button>
            ) : (
              workspaceTabs.map((tab) => {
                const active = currentPath === tab.path;
                return (
                  <div
                    key={tab.path}
                    draggable
                    onContextMenu={(e) => {
                      e.preventDefault();
                      openTabMenu(tab.path, e.clientX, e.clientY);
                    }}
                    onDragStart={() => setDraggingPath(tab.path)}
                    onDragEnd={() => setDraggingPath(null)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      if (draggingPath) reorderTabs(draggingPath, tab.path);
                      setDraggingPath(null);
                    }}
                    className={`inline-flex items-center rounded-md border text-xs ${
                      active
                        ? "border-primary/40 bg-primary/12 text-foreground"
                        : "border-border bg-surface text-muted"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => void openExistingTab(tab.path)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 transition hover:text-foreground"
                      title={tab.path}
                    >
                      {active && dirty ? (
                        <span
                          className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500"
                          aria-label="Unsaved changes"
                          title="Unsaved changes"
                        />
                      ) : null}
                      {tab.label}
                    </button>
                    <button
                      type="button"
                      onClick={() => togglePin(tab.path)}
                      className="border-l border-border px-2 py-1.5 text-muted transition hover:text-foreground"
                      aria-label={tab.pinned ? `Unpin ${tab.label}` : `Pin ${tab.label}`}
                      title={tab.pinned ? `Unpin ${tab.label}` : `Pin ${tab.label}`}
                    >
                      {tab.pinned ? (
                        <Pin className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                      ) : (
                        <PinOff className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => void closeWorkspaceTab(tab.path)}
                      className="border-l border-border px-2 py-1.5 text-muted transition hover:text-foreground"
                      aria-label={`Close ${tab.label} tab`}
                      title={`Close ${tab.label}`}
                    >
                      <X className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        openTabMenu(tab.path, rect.left, rect.bottom + 6);
                      }}
                      className="border-l border-border px-2 py-1.5 text-muted transition hover:text-foreground"
                      aria-label={`More actions for ${tab.label}`}
                      title={`More actions for ${tab.label}`}
                    >
                      <Ellipsis className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                    </button>
                  </div>
                );
              })
            )}
            </div>
            <span className="shrink-0 rounded-md border border-border bg-surface px-2 py-1 text-[11px] text-muted">
              {workspaceTabs.length}/{ADMIN_WORKSPACE_MAX_TABS}
            </span>
          </div>
          {showTabHint && workspaceTabs.length > 0 ? (
            <div className="mt-2 flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2 text-xs text-muted">
              <span>Tip: right-click a tab for quick actions.</span>
              <button
                type="button"
                onClick={dismissTabHint}
                className="ml-3 rounded border border-border px-2 py-0.5 text-[11px] hover:bg-hover-subtle hover:text-foreground"
              >
                Got it
              </button>
            </div>
          ) : null}
        </div>
        {tabMenu ? (
          <div
            ref={tabMenuRef}
            role="menu"
            className="fixed z-[70] min-w-[12rem] rounded-lg border border-border bg-topbar py-1 shadow-lg"
            style={{ left: tabMenu.x, top: tabMenu.y }}
          >
            {(() => {
              const tab = workspaceTabs.find((t) => t.path === tabMenu.path);
              if (!tab) return null;
              return (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full px-3 py-2 text-left text-sm text-foreground hover:bg-hover-subtle"
                    onClick={() => void openExistingTab(tab.path)}
                  >
                    Switch to tab
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full px-3 py-2 text-left text-sm text-foreground hover:bg-hover-subtle"
                    onClick={() => togglePin(tab.path)}
                  >
                    {tab.pinned ? "Unpin tab" : "Pin tab"}
                  </button>
                  <div className="my-1 border-t border-border" role="presentation" />
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full px-3 py-2 text-left text-sm text-foreground hover:bg-hover-subtle"
                    onClick={() => void closeWorkspaceTab(tab.path)}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full px-3 py-2 text-left text-sm text-foreground hover:bg-hover-subtle"
                    onClick={() => void closeOtherTabs(tab.path)}
                  >
                    Close others
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full px-3 py-2 text-left text-sm text-foreground hover:bg-hover-subtle"
                    onClick={() => void closeAllTabs()}
                  >
                    Close all
                  </button>
                </>
              );
            })()}
          </div>
        ) : null}

        <main ref={mainRef} className="min-h-0 flex-1 overflow-auto p-4 md:p-6">{children}</main>

        <footer className="shrink-0 border-t border-border px-6 py-3 text-right text-xs text-muted">
          © {new Date().getFullYear()} Xmegami. Admin portal.
        </footer>
        <BbFahAssistant open={bbFahOpen} onOpenChange={setBbFahOpen} />
        </div>
      </div>
    </div>
  );
}
